import { DragEvent, useState } from 'react'
import { useContext, useContextSelector } from 'use-context-selector'

import { PromoPageStateContext } from 'states/PromoPageState/PromoPageStateProvider'
import { saveSection } from 'states/PromoPageState/reducer'
import { SectionState } from 'states/PromoPageState/state'
import { countries } from 'sections/LKSection/modules/UserModule/data'
import { BlockWrap } from 'components/AdminV2/BlockWrap/BlockWrap'
import { AdminAddElemButton } from 'components/AdminV2/UI/AddElemButton/AdminAddElemButton'
import { AdminElemVisibilityToggle } from 'components/AdminV2/UI/ElemVisibilityToggle/AdminElemVisibilityToggle'
import { AdminInput } from 'components/AdminV2/UI/Input/AdminInput'
import { AdminSaveButton } from 'components/AdminV2/UI/SaveButton/AdminSaveButton'
import {
  ContractorData,
  ContractorDataItemType,
  SectionName,
  VideoMonitoringContractorDataItem,
  VideoMonitoringCountryData,
} from 'types/types'

import { ContractorBlockItem } from './ContractorBlockItem/ContractorBlockItem'

import s from './ContractorsBlock.module.scss'

enum Contractor {
  video = 'videoConnectionData',
  signboard = 'signboardData',
  outsourcing = 'outsourcingData',
}

export const ContractorsBlock = () => {
  const state = useContextSelector(PromoPageStateContext, (context) =>
    context.state.sections.find((section) => section.type === SectionName.contractors),
  ) as SectionState

  const ctx = useContext(PromoPageStateContext)

  const [localPromoState, setLocalPromoState] = useState<SectionState>(state)
  const [isVideoConnectionVisible, setIsVideoConnectionVisible] = useState(false)
  const [countryVisibility, setCountryVisibility] = useState<Record<string, boolean>>({})
  const [draggable, setDraggable] = useState(true)
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedCountry, setSelectedCountry] = useState<string>('')

  const data = state.content as ContractorData

  const changeTitleHandler = (value: string) => {
    const state = { ...localPromoState }
    state.title = value
    setLocalPromoState(state)
  }

  const addContractorHandler = (contractorType: Contractor, selectedCountry?: string) => {
    const state = { ...localPromoState }
    const data = state.content as ContractorData

    if (contractorType === Contractor.video) {
      if (!selectedCountry) {
        return
      }

      const countryData =
        data.videoConnectionData.find((country) => country.country === selectedCountry) ||
        (() => {
          const newCountryData: VideoMonitoringCountryData = {
            country: selectedCountry,
            index: data.videoConnectionData.length,
            contractors: [],
          }
          data.videoConnectionData.push(newCountryData)
          return newCountryData
        })()

      const newVideoContractor: VideoMonitoringContractorDataItem = {
        index: countryData.contractors.length,
        title: '',
        websiteLink: '',
        instructionsLink: '',
      }

      countryData.contractors.push(newVideoContractor)
    } else {
      const newGeneralContractor: ContractorDataItemType = {
        title: '',
        description: '',
        img: '',
        link: '',
        additionalLink: '',
        phone: '',
        downloadLink: '',
        email: '',
        index: data[contractorType].length,
      }

      data[contractorType].push(newGeneralContractor)
    }

    setLocalPromoState(state)
  }

  const dragStartHandler = (e: DragEvent<HTMLDivElement>, oldIndex: number) => {
    setCurrentBlockIndex(oldIndex)
  }

  const dragLeaveHandler = (e: DragEvent<HTMLDivElement>) => {
    ;(e.target as HTMLDivElement).style.border = 'none'
  }

  const dragOverHandler = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    ;(e.target as HTMLDivElement).style.border = '2px solid #6449FF'
  }

  const dragDropHandler = (
    e: DragEvent<HTMLDivElement>,
    newIndex: number,
    contractorType: Contractor,
    countryCode: string,
  ) => {
    e.preventDefault()
    ;(e.target as HTMLDivElement).style.border = 'none'

    const stateCopy = { ...localPromoState }
    const videoConnectionData = (stateCopy.content as ContractorData).videoConnectionData

    const countryData = videoConnectionData.find((data) => data.country === countryCode)
    if (!countryData) {
      return
    }

    const { contractors } = countryData
    const draggedContractor = contractors[currentBlockIndex]
    const targetContractor = contractors[newIndex]

    if (draggedContractor && targetContractor) {
      draggedContractor.index = newIndex
      targetContractor.index = currentBlockIndex

      contractors.sort((a, b) => a.index - b.index)
      setLocalPromoState(stateCopy)
    }
  }

  const handleSave = () => {
    ctx.dispatch(saveSection(localPromoState))
  }

  const toggleCountryVisibility = (countryCode: string) => {
    setCountryVisibility((prev) => ({
      ...prev,
      [countryCode]: !prev[countryCode],
    }))
  }

  return (
    <>
      <AdminInput label="Заголовок" labelWeight={600} value={localPromoState.title} onChange={changeTitleHandler} />

      <AdminElemVisibilityToggle
        text="Видеонаблюдение:"
        fontSize={16}
        className={s.contractorsVisibleToggle}
        isVisible={isVideoConnectionVisible}
        setVisible={setIsVideoConnectionVisible}
      />

      {isVideoConnectionVisible && (
        <>
          {countries.map((country) => {
            const countryData =
              data.videoConnectionData.find((c) => c.country === country.value.country) ||
              (() => {
                const newCountryData: VideoMonitoringCountryData = {
                  country: country.value.country,
                  index: data.videoConnectionData.length,
                  contractors: [],
                }
                data.videoConnectionData.push(newCountryData)
                return newCountryData
              })()

            return (
              <div key={country.value.country}>
                <AdminElemVisibilityToggle
                  text={country.label}
                  fontSize={14}
                  className={s.contractorsVisibleToggle}
                  isVisible={countryVisibility[country.value.country] || false}
                  setVisible={() => toggleCountryVisibility(country.value.country)}
                />
                {countryVisibility[country.value.country] && (
                  <>
                    {countryData.contractors.map((contractor, index) => (
                      <div key={contractor.index}>
                        <div
                          draggable={draggable}
                          onDragStart={(e: DragEvent<HTMLDivElement>) => dragStartHandler(e, index)}
                          onDragLeave={(e: DragEvent<HTMLDivElement>) => dragLeaveHandler(e)}
                          onDragOver={(e: DragEvent<HTMLDivElement>) => dragOverHandler(e)}
                          onDrop={(e: DragEvent<HTMLDivElement>) =>
                            dragDropHandler(e, index, Contractor.video, country.value.country)
                          }
                        >
                          <BlockWrap
                            type={SectionName.contractors}
                            text={`${index + 1}. ${contractor.title}`}
                            textFontSize="14px"
                            setDraggable={setDraggable}
                          >
                            <ContractorBlockItem
                              dataType="videoConnectionData"
                              index={index}
                              localPromoState={localPromoState}
                              setLocalPromoState={setLocalPromoState}
                              selectedCountry={country.value.country}
                              setSelectedCountry={setSelectedCountry}
                            />
                          </BlockWrap>
                        </div>
                      </div>
                    ))}

                    <AdminAddElemButton
                      text={`Добавить подрядчика для ${country.label}`}
                      onClick={() => addContractorHandler(Contractor.video, country.value.country)}
                    />
                  </>
                )}
              </div>
            )
          })}
        </>
      )}

      <AdminSaveButton onClick={handleSave} />
    </>
  )
}
